import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Search, X, TrendingUp } from "lucide-react";
import { searchSite, SEARCH_INDEX, type SearchEntry } from "@/lib/searchIndex";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";

const RECENT_KEY = "tms_recent_searches";
const RECENT_MAX = 5;

// Curated popular searches — surface the highest-intent services up front.
const POPULAR_HREFS = [
  "/awnings",
  "/pergola-covers",
  "/sail-shades",
  "/boat-covers",
  "/curtains",
  "/custom-fabrication",
];
const POPULAR_SUGGESTIONS: SearchEntry[] = POPULAR_HREFS
  .map((h) => SEARCH_INDEX.find((e) => e.href === h))
  .filter((e): e is SearchEntry => Boolean(e));

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = window.sessionStorage.getItem("tms_session_id");
  if (!id) {
    id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2));
    window.sessionStorage.setItem("tms_session_id", id);
  }
  return id;
}

function readRecent(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(term: string) {
  if (typeof window === "undefined") return;
  const t = term.trim();
  if (!t) return;
  try {
    const cur = readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase());
    const next = [t, ...cur].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

async function logSearch(args: {
  query: string;
  resultsCount: number;
  pagePath: string;
  clickedResult?: string | null;
}) {
  try {
    await supabase.from("search_queries").insert({
      query: args.query.slice(0, 500),
      normalized_query: args.query.trim().toLowerCase().slice(0, 500),
      results_count: args.resultsCount,
      page_path: args.pagePath,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      session_id: getSessionId(),
      clicked_result: args.clickedResult ?? null,
    });
  } catch {
    // best-effort; silently ignore
  }
}

export function SiteSearch({ className = "" }: { className?: string }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<SearchEntry[]>([]);
  const [recent, setRecent] = useState<string[]>([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const logTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reactId = useId();
  const inputId = `site-search-${reactId}`;
  const listboxId = `site-search-listbox-${reactId}`;
  const optionId = (idx: number) => `site-search-opt-${reactId}-${idx}`;

  const trimmed = q.trim();
  const showSuggestions = trimmed.length === 0;

  // Inline autocomplete ghost text — shows the rest of the top result's title
  // as a faded suffix so users can see what would complete.
  const ghost = useMemo(() => {
    if (!trimmed || results.length === 0) return "";
    const top = results[0].title;
    if (top.toLowerCase().startsWith(trimmed.toLowerCase()) && top.length > trimmed.length) {
      return top.slice(trimmed.length);
    }
    return "";
  }, [trimmed, results]);

  // Items currently shown in the dropdown (used for keyboard nav).
  const items: SearchEntry[] = showSuggestions ? POPULAR_SUGGESTIONS : results;

  useEffect(() => {
    setRecent(readRecent());
  }, []);

  useEffect(() => {
    const r = searchSite(q, 8);
    setResults(r);
    setActiveIdx(-1);

    if (logTimer.current) clearTimeout(logTimer.current);
    if (trimmed.length >= 2) {
      logTimer.current = setTimeout(() => {
        logSearch({ query: q, resultsCount: r.length, pagePath: location.pathname });
        track("search_query", {
          target: trimmed.toLowerCase(),
          metadata: { query: trimmed, results_count: r.length, placement: "header" },
        });
        if (r.length === 0) {
          track("search_no_results", {
            target: trimmed.toLowerCase(),
            metadata: { query: trimmed, placement: "header" },
          });
        }
      }, 800);
    }
    return () => {
      if (logTimer.current) clearTimeout(logTimer.current);
    };
  }, [q, location.pathname, trimmed]);

  useEffect(() => {
    function onClickAway(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  // Fire one impression per "open" (focus / typing-revealed dropdown).
  const impressionFiredRef = useRef(false);
  useEffect(() => {
    if (open && !impressionFiredRef.current) {
      impressionFiredRef.current = true;
      track("search_open", {
        metadata: {
          placement: "header",
          mode: showSuggestions ? "suggestions" : "results",
          page_path: location.pathname,
        },
      });
    } else if (!open) {
      impressionFiredRef.current = false;
    }
  }, [open, showSuggestions, location.pathname]);

  const commitNavigate = (entry: SearchEntry, source: "result" | "suggestion" | "recent") => {
    pushRecent(entry.title);
    setRecent(readRecent());
    logSearch({
      query: q || entry.title,
      resultsCount: results.length,
      pagePath: location.pathname,
      clickedResult: entry.href,
    });
    track(source === "result" ? "search_result_click" : "search_suggestion_click", {
      target: entry.href,
      metadata: {
        placement: "header",
        query: trimmed,
        title: entry.title,
        category: entry.category,
        source,
        position: items.findIndex((i) => i.href === entry.href),
        results_count: results.length,
      },
    });
    setOpen(false);
    setQ("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      const target = activeIdx >= 0 ? items[activeIdx] : items[0];
      if (target) {
        e.preventDefault();
        commitNavigate(target, showSuggestions ? "suggestion" : "result");
        navigate({ to: target.href });
      }
    } else if (e.key === "Tab" && ghost) {
      e.preventDefault();
      setQ(q + ghost);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label htmlFor={inputId} className="sr-only">Search the site</label>
      <div className="flex items-center gap-2 border border-navy-deep/20 bg-white px-3 py-2 focus-within:border-navy-deep">
        <Search className="h-4 w-4 text-navy-deep/60" aria-hidden="true" />
        <div className="relative flex-1">
          {/* Ghost autocomplete suffix */}
          {ghost && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 flex items-center whitespace-pre text-sm text-navy-deep/30"
            >
              <span className="invisible">{q}</span>
              <span>{ghost}</span>
            </div>
          )}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Search for awnings, fabrics, cities…"
            className="relative w-full bg-transparent text-sm text-navy-deep placeholder:text-navy-deep/70 focus:outline-none"
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={activeIdx >= 0 ? optionId(activeIdx) : undefined}
          />
        </div>
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(false); inputRef.current?.focus(); }}
            aria-label="Clear search"
            className="text-navy-deep/60 hover:text-navy-deep"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-auto border border-navy-deep/20 bg-white shadow-lg"
        >
          {showSuggestions ? (
            <>
              {recent.length > 0 && (
                <div>
                  <div className="px-4 pt-3 pb-1 font-display text-[10px] uppercase tracking-[0.2em] text-navy-deep/70">
                    Recent
                  </div>
                  <ul>
                    {recent.map((term) => (
                      <li key={term}>
                        <button
                          type="button"
                          onClick={() => { setQ(term); inputRef.current?.focus(); }}
                          className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-navy-deep hover:bg-navy-deep/5"
                        >
                          <Search className="h-3.5 w-3.5 text-navy-deep/70" aria-hidden="true" />
                          {term}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 font-display text-[10px] uppercase tracking-[0.2em] text-navy-deep/70">
                  <TrendingUp className="h-3 w-3" aria-hidden="true" /> Popular
                </div>
                <ul>
                  {POPULAR_SUGGESTIONS.map((r, idx) => (
                    <li key={r.href}>
                      <Link
                        id={optionId(idx)}
                        to={r.href}
                        role="option"
                        aria-selected={activeIdx === idx}
                        onClick={() => commitNavigate(r, "suggestion")}
                        className={`block border-b border-navy-deep/5 px-4 py-2.5 text-left transition ${activeIdx === idx ? "bg-navy-deep/5" : "hover:bg-navy-deep/5"}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-display text-sm font-semibold text-navy-deep">{r.title}</span>
                          <span className="text-[10px] uppercase tracking-wider text-navy-deep/70">{r.category}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-navy-deep/70">
              No matches for &ldquo;{q}&rdquo;. Try a service, fabric, or city.
            </div>
          ) : (
            <ul>
              {results.map((r, idx) => (
                <li key={r.href}>
                  <Link
                    id={optionId(idx)}
                    to={r.href}
                    role="option"
                    aria-selected={activeIdx === idx}
                    onClick={() => commitNavigate(r, "result")}
                    className={`block border-b border-navy-deep/5 px-4 py-3 text-left transition ${activeIdx === idx ? "bg-navy-deep/5" : "hover:bg-navy-deep/5"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-display text-sm font-semibold text-navy-deep">{r.title}</span>
                      <span className="text-[10px] uppercase tracking-wider text-navy-deep/70">{r.category}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-navy-deep/70">{r.description}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
